import { makeStyles } from '@material-ui/core/styles';

/**
 * The shared approval level chain.
 *
 * Sized for the narrowest consumer — the 240px rail both offer drawers give it —
 * so a wider surface can offer more room without it having to grow. Spacing is on
 * the 2px grid, 8px inside a level because its heading, rows and notes are one
 * unit. Colours come from the FDS theme; no raw hex.
 */
export const useStyles = makeStyles(theme => ({
  chain: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  ruleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  ruleName: {
    ...theme.typography.body2,
    color: theme.palette.common.gray800,
  },
  level: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingBottom: 12,
  },
  // The level's name and its own state on one line: the level's answer is read
  // before the rows that led to it.
  levelHeadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  /**
   * The design's uppercase "LEVEL N" heading.
   *
   * Uppercased here rather than by a typography role, which `content-hierarchy`
   * reserves to the caption role. That rule describes the Ripple token scale; in
   * recruiter-ui's MUI v4 theme `caption` is 14px with `textTransform: none` and
   * `overline` is 12px with it explicitly off, so no role carries it. The
   * alternative was a pre-uppercased string, which breaks every non-Latin locale.
   */
  levelHeading: {
    ...theme.typography.overline,
    // The medium face and black, both from the prototype, which draws this label
    // as a heading over its group rather than as a quiet note beside it. Black is
    // a token in its own right here — Ripple carries `--color-black` separately
    // from `--color-neutral-900`, and `theme.ts` remaps neither — so this is the
    // design's own value rather than the nearest neutral to it.
    fontFamily: theme.typography.h4.fontFamily,
    lineHeight: 1.5,
    color: theme.palette.common.black,
    textTransform: 'uppercase',
    // 0.8px at this role's 12px. Kept in em so it tracks the size if the role
    // ever moves.
    letterSpacing: '0.067em',
  },
  // A compact heading row holds only the date, and a long level name wraps
  // beside it. Aligned to the name's first line, the date reads as the
  // heading's own rather than as a note floating between its lines.
  levelHeadingRowCompact: {
    alignItems: 'baseline',
  },
  // A level or rule name with no space in it breaks inside the compact rail,
  // as an approver's name does, rather than running past it. Compact only:
  // beside a level pill, the other rails would break an ordinary long word
  // mid-way instead.
  compactWrap: {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  // Screen-reader-only. Same declarations as the other places in this app that
  // need it (`ApprovalRules/styles`, `OfferLetterTemplates/CountRatio`).
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  levelNote: {
    color: theme.palette.common.gray600,
  },
  levelWarning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    color: theme.palette.common.orange800,
  },
  warningIcon: {
    fontSize: 16,
    color: theme.palette.common.orange600,
    flexShrink: 0,
    marginTop: 2,
  },
  approverRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  // The pill and, under it, when the thing the pill names happened. Right-edge
  // aligned so a column of pills stays a column once the dates vary in width.
  statusColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
    flexShrink: 0,
  },
  timestamp: {
    color: theme.palette.common.gray600,
    whiteSpace: 'nowrap',
  },
  // Breaks a name with no space in it, the way `approverComment` breaks a
  // reason, rather than running it under the pill beside it.
  approverIdentity: {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  // A compact name is a 16px line beside a 26px pill, and the row aligns to the
  // top so a column of pills stays level. Starting the name at the pill's own
  // 4px inset puts it on the line of the pill's word.
  approverNameCompact: {
    paddingTop: 4,
  },
  // The reason a rejecting approver gave, indented under their row so it reads
  // as theirs rather than as a note on the level.
  approverComment: {
    marginTop: 4,
    color: theme.palette.common.gray800,
    fontStyle: 'italic',
    overflowWrap: 'anywhere',
  },
  // Color only: the size comes from the overline variant, and a size set here
  // would tie with that variant's own class. Secondary text is 8.6:1 on white.
  // The prototype's neutral-400 is 4.9:1, which passes AA but not the AAA
  // secondary text needs. Follows `approverComment` so it wins the color.
  approverCommentCompact: {
    color: theme.palette.text.secondary,
  },

  // Colour and a word, never colour alone. The glyph is the third cue.
  pill: {
    ...theme.typography.overline,
    // The prototype's pill is a tag, not a lozenge: a 4px corner on 4px 8px of
    // padding, with the state word on the medium face.
    fontFamily: theme.typography.h4.fontFamily,
    lineHeight: 1.5,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    borderRadius: 4,
    padding: '4px 8px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  pillApproved: {
    background: theme.palette.common.green100,
    color: theme.palette.common.green800,
  },
  pillRejected: {
    background: theme.palette.common.red100,
    color: theme.palette.common.red800,
  },
  pillNeutral: {
    background: theme.palette.common.gray200,
    color: theme.palette.common.gray800,
  },
  pillIcon: {
    fontSize: 14,
  },
}));
